import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../supabase';
import { Icon } from './Icon.js';
import SearchBar from './SearchBar';
import ProtocolShield from './ProtocolShield';
import JellyfishLoader from './JellyfishLoader';
import { getUserFacingErrorMessage } from '../utils/sanitizeErrorForLog';
import './SharePostModal.css';

const SharePostModal = ({ isOpen, onClose, post, onShare }) => {
  const [shareTitle, setShareTitle] = useState('');
  const [shareText, setShareText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('repost'); // 'repost' or 'message'
  
  // Message mode state
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  
  // Disclaimer modal state
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [disclaimerText, setDisclaimerText] = useState('');
  const [disclaimerVersion, setDisclaimerVersion] = useState('');
  const [acceptResponsibility, setAcceptResponsibility] = useState(false);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  
  // Extract protocols from post
  const protocols = useMemo(() => {
    if (post.attachedProtocols && post.attachedProtocols.length > 0) {
      return post.attachedProtocols;
    }
    
    if (post.post_attachments) {
      return post.post_attachments
        .filter(att => att.type === 'protocols')
        .flatMap(att => (att.post_attachment_protocols || [])
          .map(pap => pap.protocols)
          .filter(Boolean));
    }
    
    return [];
  }, [post]);
  
  const hasProtocols = protocols.length > 0;
  const protocolToShare = hasProtocols ? protocols[0] : null;

  // Fetch current user
  useEffect(() => {
    const fetchUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setCurrentUser(session?.user || null);
    };
    fetchUser();
  }, []);

  // Set initial mode based on content
  useEffect(() => {
    // If we have a protocol, we could default to message, but repost is standard behavior
    // Keeping 'repost' as default, user can switch tab
  }, [hasProtocols]);

  // Fetch users for message mode - only when there's a search query
  useEffect(() => {
    if (mode === 'message' && currentUser && searchQuery.trim()) {
      fetchUsers();
    } else if (mode === 'message' && currentUser && !searchQuery.trim()) {
      // Clear users when search is empty
      setUsers([]);
    }
  }, [mode, searchQuery, currentUser]);

  const fetchUsers = async () => {
    if (!currentUser || !searchQuery.trim()) return;
    
    setLoadingUsers(true);
    try {
      // Search users
      const { data: searchResults, error: searchError } = await supabase
        .from('user_profiles')
        .select('user_id, username, profile_picture_url, bio')
        .ilike('username', `%${searchQuery}%`)
        .neq('user_id', currentUser.id)
        .limit(10);
        
      if (searchError) throw searchError;
      
      // Check which ones are followed
      if (searchResults.length > 0) {
          const userIds = searchResults.map(u => u.user_id);
          const { data: follows } = await supabase
              .from('follows')
              .select('followee_id')
              .eq('follower_id', currentUser.id)
              .in('followee_id', userIds);
          
          const followedIds = new Set((follows || []).map(f => f.followee_id));
          
          const usersData = searchResults.map(u => ({
              ...u,
              isFollowing: followedIds.has(u.user_id)
          })).sort((a, b) => {
              // Sort followed users first
              if (a.isFollowing && !b.isFollowing) return -1;
              if (!a.isFollowing && b.isFollowing) return 1;
              return 0;
          });
          
          setUsers(usersData);
      } else {
          setUsers([]);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setShareTitle('');
      setShareText('');
      setError('');
      setMode('repost');
      setSelectedUser(null);
      setSearchQuery('');
      setIsSearchFocused(false);
      onClose();
    }
  };

  const handleUserSelect = (user) => {
    setSelectedUser(user);
    setSearchQuery('');
  };

  const handleUserRemove = () => {
    setSelectedUser(null);
  };

  // Check if user has accepted the messages disclaimer
  // Returns true if user has accepted (don't show modal), false if should show modal
  // Checks the most recent record since we always insert new rows
  const checkDisclaimerAcceptance = async (userId) => {
    if (!userId) {
      return false;
    }
    try {
      const { data, error } = await supabase
        .from('disclaimers_log')
        .select('id, accepted_at, do_not_show_again')
        .eq('user_id', userId)
        .eq('disclaimer_type', 'messages')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        return false; // Show modal on error
      }

      // Show modal if no record exists (user hasn't accepted yet)
      if (!data) {
        return false; // Should show modal
      }
      
      // Don't show modal only if do_not_show_again is true
      // Even if user accepted before, if they didn't check "do not show again", show modal again
      if (data.do_not_show_again === true) {
        return true; // Don't show modal
      }
      
      // Show modal if do_not_show_again is false or null
      return false; // Should show modal
    } catch (error) {
      return false; // Show modal on error
    }
  };

  // Fetch disclaimer text from disclaimers table
  const fetchDisclaimerText = async () => {
    try {
      const { data, error } = await supabase
        .from('disclaimers')
        .select('text, version')
        .eq('type', 'messages')
        .eq('latest', true)
        .maybeSingle();
      
      if (error) {
        return null;
      }

      if (!data) {
        return null;
      }

      return data;
    } catch (error) {
      return null;
    }
  };

  // Store disclaimer acceptance - always inserts new row for audit trail
  const storeDisclaimerAcceptance = async (userId, version, doNotShow) => {
    if (!userId) return false;
    try {
      // Always insert a new record (creates audit trail)
      const { error } = await supabase
        .from('disclaimers_log')
        .insert({
          user_id: userId,
          disclaimer_type: 'messages',
          disclaimer_version: version,
          accepted_at: new Date().toISOString(),
          do_not_show_again: doNotShow
        });

      if (error) {
        console.error('Error storing disclaimer acceptance:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error storing disclaimer acceptance:', error);
      return false;
    }
  };

  // Internal function to perform message sending (after disclaimer check)
  const sendMessageInternal = async () => {
    if (!currentUser || !selectedUser) return;

    // Create message
    const { data: newMessage, error: msgError } = await supabase
      .from('messages')
      .insert({ 
        sender_id: currentUser.id, 
        receiver_id: selectedUser.user_id, 
        content: shareText.trim() || 'Shared a protocol', 
        reply_to_message_id: null 
      })
      .select('id')
      .single();

    if (msgError) throw msgError;

    // Attach protocol
    if (protocolToShare && newMessage?.id) {
        // Create message_attachment record
        const { data: attachmentData, error: attachmentError } = await supabase
          .from('message_attachments')
          .insert({
            message_id: newMessage.id,
            type: 'protocols',
            sort_order: 0,
            source_post_id: post?.id ?? null
          })
          .select('id')
          .single();

        if (attachmentError) throw attachmentError;

        if (attachmentData?.id) {
          // Create message_attachment_protocols record
          // First check if it already exists to avoid any ON CONFLICT issues
          const { data: existing } = await supabase
            .from('message_attachment_protocols')
            .select('id')
            .eq('message_attachment_id', attachmentData.id)
            .eq('protocol_id', protocolToShare.id)
            .maybeSingle();

          if (!existing) {
            // Only insert if it doesn't already exist
            const { error: protocolError } = await supabase
              .from('message_attachment_protocols')
              .insert({
                message_attachment_id: attachmentData.id,
                protocol_id: protocolToShare.id,
                sort_order: 0
              });

            if (protocolError) {
              throw protocolError;
            }
          }
          // If it already exists, silently skip (duplicate prevention)
        }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      if (mode === 'repost') {
        // Call the onShare callback with the title and share text
        await onShare(shareText.trim() || '', shareTitle.trim() || '');
      } else {
        // Send message with protocol - check disclaimer first
        if (!selectedUser) {
            throw new Error('Please select a user to message');
        }
        if (!protocolToShare) {
            throw new Error('No protocol to share');
        }
        
        // Check if user has accepted the disclaimer
        const hasAcceptedDisclaimer = await checkDisclaimerAcceptance(currentUser.id);
        
        if (!hasAcceptedDisclaimer) {
          // Fetch disclaimer text and show modal
          const disclaimer = await fetchDisclaimerText();
          if (disclaimer && disclaimer.text) {
            setDisclaimerText(disclaimer.text);
            setDisclaimerVersion(disclaimer.version || 'v1.0');
            setShowDisclaimerModal(true);
            setAcceptResponsibility(false);
            setDoNotShowAgain(false);
            setIsSubmitting(false);
            return;
          }
        }

        // User has accepted disclaimer, proceed with sending
        await sendMessageInternal();
      }
      
      setShareTitle('');
      setShareText('');
      handleClose();
    } catch (err) {
      console.error('Error sharing post:', err);
      setError(getUserFacingErrorMessage(err, 'Failed to share post. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle disclaimer acceptance
  const handleDisclaimerAccept = async () => {
    if (!acceptResponsibility) {
      // Required checkbox not checked
      return;
    }

    try {
      if (!currentUser) {
        setShowDisclaimerModal(false);
        setIsSubmitting(false);
        return;
      }

      // Store disclaimer acceptance in database
      const acceptanceStored = await storeDisclaimerAcceptance(currentUser.id, disclaimerVersion, doNotShowAgain);
      
      if (acceptanceStored) {
        setShowDisclaimerModal(false);
        setAcceptResponsibility(false);
        setDoNotShowAgain(false);
        
        // User has accepted disclaimer, proceed with sending
        try {
          await sendMessageInternal();
          setShareText('');
          handleClose();
        } catch (err) {
          console.error('Error sending message:', err);
          setError(getUserFacingErrorMessage(err, 'Failed to send message. Please try again.'));
        }
      } else {
        alert('Error storing disclaimer acceptance. Please try again.');
      }
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error handling disclaimer acceptance:', error);
      alert('Error storing disclaimer acceptance. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleDisclaimerCancel = () => {
    // Just close the modal, nothing changes
    setShowDisclaimerModal(false);
    setAcceptResponsibility(false);
    setDoNotShowAgain(false);
    setIsSubmitting(false);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="share-modal-overlay" onClick={handleClose} role="dialog" aria-modal="true" aria-labelledby="share-modal-title">
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="share-modal-header">
          <h2 id="share-modal-title" className="share-modal-title">Share Post</h2>
          <button className="share-modal-close" onClick={handleClose} disabled={isSubmitting}>
            <Icon name="X" className="icon--sm" />
          </button>
        </div>

        {hasProtocols && (
            <div className="share-modal-tabs">
                <button 
                    className={`share-modal-tab ${mode === 'repost' ? 'active' : ''}`}
                    onClick={() => setMode('repost')}
                >
                    Share to Feed
                </button>
                <button 
                    className={`share-modal-tab ${mode === 'message' ? 'active' : ''}`}
                    onClick={() => setMode('message')}
                >
                    Send Protocol via Message
                </button>
            </div>
        )}

        <div className="share-modal-content">
          <form onSubmit={handleSubmit} className="share-form">
            
            {mode === 'message' && (
                <div className="share-message-container">
                    <label className="share-form-label">To:</label>
                    
                    {!selectedUser ? (
                        <div className="share-search-wrapper">
                            <SearchBar 
                                value={searchQuery}
                                onChange={setSearchQuery}
                                placeholder="Search for people..."
                                onFocus={() => setIsSearchFocused(true)}
                                onBlur={() => setIsSearchFocused(false)}
                            />
                            
                            {isSearchFocused && searchQuery.trim() && (
                                <div 
                                    className="share-user-list-dropdown"
                                    onMouseDown={(e) => e.preventDefault()}
                                >
                                    {loadingUsers ? (
                                        <div className="share-loading-compact">
                                            <JellyfishLoader size={40} />
                                        </div>
                                    ) : users.length === 0 ? (
                                        <div className="share-empty-state">
                                            <span>No users found</span>
                                        </div>
                                    ) : (
                                        users.map(user => (
                                            <div 
                                                key={user.user_id} 
                                                className="share-user-item-compact"
                                                onClick={() => handleUserSelect(user)}
                                            >
                                                <div className="share-user-avatar-compact">
                                                    {user.profile_picture_url ? (
                                                        <img src={user.profile_picture_url} alt={user.username} />
                                                    ) : (
                                                        <span>{(user.username || 'U').charAt(0).toUpperCase()}</span>
                                                    )}
                                                </div>
                                                <div className="share-user-info-compact">
                                                    <span className="share-user-name-compact">{user.username}</span>
                                                    {user.isFollowing && <span className="share-user-badge">Following</span>}
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="share-selected-recipient">
                            <div className="share-recipient-chip">
                                <div className="share-recipient-avatar">
                                    {selectedUser.profile_picture_url ? (
                                        <img src={selectedUser.profile_picture_url} alt={selectedUser.username} />
                                    ) : (
                                        <span>{(selectedUser.username || 'U').charAt(0).toUpperCase()}</span>
                                    )}
                                </div>
                                <span className="share-recipient-name">{selectedUser.username}</span>
                                <button 
                                    type="button" 
                                    className="share-recipient-remove"
                                    onClick={handleUserRemove}
                                >
                                    <Icon name="X" className="icon--xs" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {mode === 'repost' && (
                <div className="share-input-section">
                    <label className="share-form-label">Title</label>
                    <input
                        type="text"
                        className="share-title-input"
                        value={shareTitle}
                        onChange={(e) => setShareTitle(e.target.value)}
                        placeholder="Give your repost a title"
                        maxLength={500}
                        disabled={isSubmitting}
                    />
                    <div className="share-character-count">
                        {shareTitle.length}/500
                    </div>
                </div>
            )}

            {(mode === 'repost' || mode === 'message') && (
                <div className="share-input-section">
                    <label className="share-form-label">
                        {mode === 'message' ? 'Message (optional)' : 'Add your thoughts (optional)'}
                    </label>
                    <textarea
                        className="share-textarea"
                        value={shareText}
                        onChange={(e) => setShareText(e.target.value)}
                        placeholder={mode === 'message' ? "Write a message..." : "What are your thoughts?"}
                        rows={4}
                        maxLength={500}
                        disabled={isSubmitting}
                        autoFocus={mode === 'repost' || (mode === 'message' && !!selectedUser)}
                    />
                    <div className="share-character-count">
                        {shareText.length}/500
                    </div>

                    {/* Preview Area */}
                    {mode === 'repost' ? (
                        /* Original Post Preview for Repost */
                        <div className="share-original-post-preview">
                            <div className="share-original-post-header">
                                <div className="share-original-post-avatar">
                                    {post.user_profiles?.profile_picture_url ? (
                                        <img 
                                            src={post.user_profiles.profile_picture_url} 
                                            alt={`${post.user_profiles?.username || 'User'}'s profile`}
                                            className="share-original-post-avatar-image"
                                        />
                                    ) : (
                                        <span className="share-original-post-avatar-fallback">
                                            {(post.user_profiles?.username || 'U').charAt(0).toUpperCase()}
                                        </span>
                                    )}
                                </div>
                                <div className="share-original-post-user-info">
                                    <span className="share-original-post-username">
                                        {post.user_profiles?.username || 'User'}
                                    </span>
                                </div>
                            </div>
                            {post.title && (
                                <div className="share-original-post-title">{post.title}</div>
                            )}
                            <div className="share-original-post-content">
                                {post.content?.length > 150 
                                ? `${post.content.substring(0, 150)}...` 
                                : post.content
                                }
                            </div>
                        </div>
                    ) : (
                        /* Message Bubble Preview with Protocol Shield inside */
                        <div className="share-message-preview">
                            <div className="share-message-bubble">
                                {shareText && <div className="share-message-text">{shareText}</div>}
                                {protocolToShare && (
                                    <div className="share-message-protocols">
                                        <div className="share-message-protocol-item">
                                            <ProtocolShield 
                                                protocol={protocolToShare}
                                                size="small"
                                                disableFlip={true}
                                                hideFabMenu={true}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {error && (
              <div className="share-error">
                <Icon name="AlertCircle" className="icon--sm icon--danger" />
                {error}
              </div>
            )}

            <div className="share-modal-actions">
              <button
                type="button"
                className="btn btn--tertiary"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn--primary"
                disabled={isSubmitting || (mode === 'message' && !selectedUser)}
              >
                {isSubmitting ? 'Sharing...' : (mode === 'message' ? 'Send Message' : 'Share')}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Messages Disclaimer Modal */}
      {showDisclaimerModal && (
        <div className="modal-overlay" onClick={(e) => {
          // Don't close on overlay click - user must explicitly choose
          e.stopPropagation();
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', zIndex: 10000 }}>
            <div className="modal-header">
              <h3>Messages Disclaimer</h3>
            </div>
            <div className="modal-body" style={{ padding: '24px' }}>
              <div style={{ 
                marginBottom: '24px', 
                fontSize: '14px', 
                lineHeight: '1.6', 
                color: 'var(--color-text-primary)',
                whiteSpace: 'pre-line'
              }}>
                {disclaimerText}
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '12px', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: 'var(--color-text-primary)'
                }}>
                  <input
                    type="checkbox"
                    checked={acceptResponsibility}
                    onChange={(e) => setAcceptResponsibility(e.target.checked)}
                    style={{ 
                      marginTop: '2px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  />
                  <span>I understand and accept responsibility for sending this message</span>
                </label>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '12px', 
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: 'var(--color-text-primary)'
                }}>
                  <input
                    type="checkbox"
                    checked={doNotShowAgain}
                    onChange={(e) => setDoNotShowAgain(e.target.checked)}
                    style={{ 
                      marginTop: '2px',
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  />
                  <span>Do not show again</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  className="btn btn--secondary btn--md btn--rounded"
                  onClick={handleDisclaimerCancel}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn--primary btn--md btn--rounded"
                  onClick={handleDisclaimerAccept}
                  disabled={!acceptResponsibility}
                  style={{ flex: 1 }}
                >
                  Accept
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default SharePostModal;
